import { useState, useCallback, useMemo } from 'react';
import moment from 'moment';
import {
  ActiveCustomer,
  DateRangeState,
  getWorkspaceId,
  useAppSelector,
  useLazyGetActiveCustomersQuery,
} from '@zonofi/zono-money-store';
import { usePaginatedList, useUrlDateRange } from '../../hooks';

export const useActiveCustomersList = () => {
  const [showCalender, setShowCalender] = useState(false);
  const [dateRangeSelection, setDateRangeSelection] =
    useState<DateRangeState | null>(null);

  const { appliedDateRange, updateRange } = useUrlDateRange();

  const sellerWorkspaceId = useAppSelector(getWorkspaceId);
  const [getActiveCustomersApi, activeCustomersApiResponse] =
    useLazyGetActiveCustomersQuery();

  const fetchActiveCustomers = useCallback(
    (params: { pageNo: number; pageSize: number }) => {
      getActiveCustomersApi({
        sellerWorkspaceId,
        startDate: moment(appliedDateRange.startDate)
          .startOf('day')
          .format('YYYY-MM-DD'),
        endDate: moment(appliedDateRange.endDate)
          .add(1, 'day')
          .startOf('day')
          .format('YYYY-MM-DD'),
        ...params,
      });
    },
    [appliedDateRange]
  );

  const memoizedActiveCustomers = useMemo(
    () => activeCustomersApiResponse?.data?.activeUsers ?? [],
    [activeCustomersApiResponse?.data?.activeUsers]
  );

  const {
    data: activeCustomer,
    lastItemRef,
    resetAll,
    pageNo,
    onScroll,
    isPageLoading,
    isEndOfList,
  } = usePaginatedList<ActiveCustomer>({
    fetchDataApiCall: fetchActiveCustomers,
    apiResponseData: memoizedActiveCustomers,
    isLoading: activeCustomersApiResponse.isLoading,
    isError: activeCustomersApiResponse.isError,
    uniqueKey: 'inviteId',
    dependencies: [
      appliedDateRange?.startDate.toString(),
      appliedDateRange?.endDate.toString(),
    ],
    paginationMeta: {
      startRecord:
        activeCustomersApiResponse?.data?.pagination?.startRecord || 0,
      endRecord: activeCustomersApiResponse?.data?.pagination?.endRecord || 0,
      totalCount: activeCustomersApiResponse?.data?.pagination?.totalCount || 0,
    },
  });

  const handleSelect = (ranges: any) => {
    const newRange: DateRangeState = {
      key: 'selection',
      startDate: moment(ranges.selection.startDate).startOf('day').toDate(),
      endDate: moment(ranges.selection.endDate).endOf('day').toDate(),
    };
    setDateRangeSelection(newRange);
  };

  const applyNewDateRange = () => {
    if (dateRangeSelection) {
      updateRange(dateRangeSelection);
      setShowCalender(false);
      resetAll();
    }
  };

  return {
    showCalender,
    setShowCalender,
    dateRangeSelection: dateRangeSelection ?? appliedDateRange,
    handleSelect,
    applyNewDateRange,
    activeCustomer,
    lastItemRef,
    isPageLoading,
    fetchActiveCustomers,
    setDateRangeSelection,
    pageNo,
    onScroll,
    isLoading:
      pageNo === 1 &&
      (activeCustomersApiResponse.isUninitialized ||
        activeCustomersApiResponse.isLoading),
    isEndOfList,
  };
};
